import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Navigation, Clock, Gauge, Battery, Signal, Car, X, Filter } from 'lucide-react';
import { formatBatteryDisplay } from '../../utils/telemetry';

const getDeviceTelemetrySources = (device) => [
  device?.liveData,
  device?.currentLocation,
  device,
];

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function GroupMapTracker({
  devicesOnMap, selectedDevices, setSelectedDevices,
  deviceAddresses, getDirections, formatTimestamp12h,
  handleOpenCommandModal, routeStart, routeEnd,
  routeDetails, setRouteDetails, showFilter,
  setShowFilter, locationError, showLocationError,
  setShowLocationError, deviceCounts, vehicleTypeIcons,
  getVehicleIcon
}) {
  const [mapCenter] = useState([28.6139, 77.2090]); // Default: Delhi
  const [mapZoom] = useState(12);

  // Create custom icons for different statuses
  const createCustomIcon = (status, vehicleType) => {
    const colors = {
      online: '#48bb78',
      offline: '#e53e3e',
      unknown: '#ed8936'
    };

    return L.divIcon({
      className: 'custom-marker',
      html: `
        <div style="
          background: ${colors[status] || colors.unknown};
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          color: white;
          font-size: 18px;
        ">
          🚗
        </div>
      `,
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });
  };

  const handleDeviceSelect = (deviceId) => {
    setSelectedDevices(prev => {
      if (prev.includes(deviceId)) {
        return prev.filter(id => id !== deviceId);
      } else {
        return [...prev, deviceId];
      }
    });
  };

  const handleSetRouteStart = (device) => {
    const lat = device.currentLocation?.latitude;
    const lng = device.currentLocation?.longitude;
    if (typeof lat !== 'number' || typeof lng !== 'number') return;
    getDirections(
      { lat, lng },
      routeEnd || { lat: lat + 0.01, lng: lng + 0.01 }
    );
  };

  const clearRoute = () => {
    setRouteDetails(null);
  };

  return (
    <div style={{ display: 'flex', gap: '20px', height: 'calc(100vh - 120px)' }}>
      
      {/* Device Filter Sidebar */}
      {showFilter && (
        <div className="card" style={{ width: '320px', maxHeight: '100%', overflowY: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 className="card-title" style={{ marginBottom: 0, fontSize: '16px' }}>
              Devices ({devicesOnMap.length})
            </h3>
            <button className="btn-icon" onClick={() => setShowFilter(false)}>
              <X size={18} />
            </button>
          </div>

          {/* Status Filter Summary */}
          <div style={{ marginBottom: '16px', padding: '12px', background: '#f7fafc', borderRadius: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '8px' }}>
              <span style={{ color: '#48bb78', fontWeight: '600' }}>● Online: {deviceCounts.online}</span>
              <span style={{ color: '#e53e3e', fontWeight: '600' }}>● Offline: {deviceCounts.offline}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#ed8936', fontWeight: '600' }}>
              ● Unknown: {deviceCounts.unknown}
            </div>
          </div>

          {/* Device List */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {devicesOnMap.map(device => (
              <div
                key={device._id}
                style={{
                  padding: '12px',
                  border: selectedDevices.includes(device._id) ? '2px solid #667eea' : '1px solid #e2e8f0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  background: selectedDevices.includes(device._id) ? 'rgba(102, 126, 234, 0.05)' : 'white',
                  transition: 'all 0.2s ease'
                }}
                onClick={() => handleDeviceSelect(device._id)}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                  <div>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: '#2d3748' }}>
                      {device.name}
                    </div>
                    <div style={{ fontSize: '11px', color: '#718096', fontFamily: 'monospace' }}>
                      {device.imei}
                    </div>
                  </div>
                  <span className={`status-badge ${device.status}`} style={{ fontSize: '10px', padding: '4px 8px' }}>
                    {device.status}
                  </span>
                </div>

                {device.assignedToUser && (
                  <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '4px' }}>
                    👤 {device.assignedToUser.username}
                  </div>
                )}

                <div style={{ fontSize: '11px', color: '#718096' }}>
                  📍 {deviceAddresses[device._id] || 'Loading address...'}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Map Container */}
      <div className="card" style={{ flex: 1, padding: '0', overflow: 'hidden', position: 'relative' }}>
        {!showFilter && (
          <button
            className="btn-primary"
            onClick={() => setShowFilter(true)}
            style={{ position: 'absolute', top: '16px', left: '16px', zIndex: 1000 }}
          >
            <Filter size={18} />
            Show Devices
          </button>
        )}

        {routeDetails && (
          <div style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            zIndex: 1000,
            background: 'white',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h4 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>Route Details</h4>
              <button className="btn-icon" onClick={clearRoute}>
                <X size={16} />
              </button>
            </div>
            <div style={{ fontSize: '13px' }}>
              <div style={{ marginBottom: '6px' }}>
                📏 Distance: <strong>{routeDetails.distance} km</strong>
              </div>
              <div>
                ⏱️ Duration: <strong>{routeDetails.duration} mins</strong>
              </div>
            </div>
          </div>
        )}

        <MapContainer
          center={mapCenter}
          zoom={mapZoom}
          style={{ height: '100%', width: '100%', borderRadius: '16px' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Device Markers */}
          {devicesOnMap
            .filter(device => selectedDevices.length === 0 || selectedDevices.includes(device._id))
            .map(device => (
              <Marker
                key={device._id}
                position={[device.currentLocation.latitude, device.currentLocation.longitude]}
                icon={createCustomIcon(device.status, device.vehicleType)}
              >
                <Popup maxWidth={300}>
                  <div style={{ padding: '8px' }}>
                    <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '700', color: '#2d3748' }}>
                      {device.name}
                    </h3>
                    
                    <div style={{ display: 'grid', gap: '8px', fontSize: '13px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Car size={14} style={{ color: '#667eea' }} />
                        <span><strong>IMEI:</strong> {device.imei}</span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Gauge size={14} style={{ color: '#667eea' }} />
                        <span><strong>Speed:</strong> {device.liveData?.speed || 0} km/h</span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Navigation size={14} style={{ color: '#667eea' }} />
                        <span><strong>Heading:</strong> {device.liveData?.heading || 0}°</span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Battery size={14} style={{ color: '#667eea' }} />
                        <span><strong>Battery:</strong> {formatBatteryDisplay(getDeviceTelemetrySources(device))}</span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Signal size={14} style={{ color: '#667eea' }} />
                        <span><strong>GSM:</strong> {device.liveData?.gsmSignal || 'N/A'}</span>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Clock size={14} style={{ color: '#667eea' }} />
                        <span><strong>Updated:</strong> {formatTimestamp12h(device.liveData?.timestamp)}</span>
                      </div>
                    </div>

                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: '12px', color: '#718096', marginBottom: '8px' }}>
                        📍 {deviceAddresses[device._id] || 'Loading address...'}
                      </div>
                      
                      {device.assignedToUser && (
                        <div style={{ fontSize: '12px', color: '#4a5568', marginBottom: '8px' }}>
                          👤 Assigned to: <strong>{device.assignedToUser.username}</strong>
                        </div>
                      )}
                    </div>

                    <button
                      className="btn-primary"
                      onClick={() => handleOpenCommandModal(device)}
                      style={{ width: '100%', marginTop: '8px', fontSize: '13px', padding: '8px' }}
                    >
                      Send Command
                    </button>
                  </div>
                </Popup>
              </Marker>
            ))}

          {/* Route Polyline */}
          {routeDetails && routeDetails.geometry && (
            <Polyline
              positions={routeDetails.geometry.coordinates.map(coord => [coord[1], coord[0]])}
              color="#667eea"
              weight={4}
              opacity={0.7}
            />
          )}
        </MapContainer>
      </div>
    </div>
  );
}

export default GroupMapTracker;
